import { useEffect, useState, type FormEvent } from 'react';
import { submitLead } from '../lib/leads';

interface DemoModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Optional initial value for the "what are you trying to train?" textarea.
   * Used by the Pricing page Enterprise CTA to seed an "Interested in
   * Enterprise pricing" note. Re-applied each time the modal opens.
   */
  initialNotes?: string;
}

type Status = 'idle' | 'submitting' | 'success' | 'error';

export function DemoModal({
  open,
  onClose,
  initialNotes = '',
}: DemoModalProps): JSX.Element | null {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [organization, setOrganization] = useState('');
  const [role, setRole] = useState('');
  const [vertical, setVertical] = useState('');
  const [notes, setNotes] = useState(initialNotes);
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!open) return;
    // Seed the notes field whenever the modal is (re)opened so callers can
    // prefill different copy (e.g. "Interested in Enterprise pricing").
    setNotes(initialNotes);
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose, initialNotes]);

  if (!open) return null;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg('');
    try {
      await submitLead({
        type: 'demo',
        name,
        email,
        phone: phone || undefined,
        organization,
        role,
        vertical,
        notes,
      });
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong.');
    }
  }

  function reset() {
    setName('');
    setEmail('');
    setPhone('');
    setOrganization('');
    setRole('');
    setVertical('');
    setNotes('');
    setStatus('idle');
    setErrorMsg('');
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="demo-title"
    >
      <button
        type="button"
        aria-label="Close dialog"
        onClick={reset}
        className="absolute inset-0 bg-navy-950/70 backdrop-blur-sm cursor-default"
      />
      <div className="relative w-full max-w-lg rounded-xl bg-white shadow-2xl ring-1 ring-navy-900/5 max-h-[92vh] overflow-y-auto">
        <div className="px-6 py-5 border-b border-steel-200 flex items-center justify-between sticky top-0 bg-white/95 backdrop-blur-sm z-10">
          <h2 id="demo-title" className="text-lg font-extrabold tracking-tight text-navy-900">
            Request a demo
          </h2>
          <button
            type="button"
            onClick={reset}
            className="text-steel-400 hover:text-steel-700 transition-colors p-1 -m-1"
            aria-label="Close"
          >
            <svg viewBox="0 0 20 20" className="h-5 w-5" fill="currentColor" aria-hidden="true">
              <path d="M5.3 5.3a1 1 0 0 1 1.4 0L10 8.6l3.3-3.3a1 1 0 1 1 1.4 1.4L11.4 10l3.3 3.3a1 1 0 1 1-1.4 1.4L10 11.4l-3.3 3.3a1 1 0 1 1-1.4-1.4L8.6 10 5.3 6.7a1 1 0 0 1 0-1.4Z" />
            </svg>
          </button>
        </div>

        {status === 'success' ? (
          <div className="px-6 py-10 text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-cyan-500/15 flex items-center justify-center">
              <svg viewBox="0 0 20 20" className="h-6 w-6 text-cyan-500" fill="currentColor" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.4 7.4a1 1 0 0 1-1.4 0L3.3 9.5a1 1 0 1 1 1.4-1.4l3.9 3.9 6.7-6.7a1 1 0 0 1 1.4 0Z"
                />
              </svg>
            </div>
            <h3 className="mt-4 text-xl font-bold text-navy-900">Got it.</h3>
            <p className="mt-2 text-sm text-steel-600">
              We&rsquo;ll be in touch within one business day to schedule your walkthrough.
            </p>
            <button type="button" onClick={reset} className="btn btn-primary mt-6">
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="px-6 py-5 space-y-4">
            <Field
              id="d-name"
              label="Name"
              required
              value={name}
              onChange={setName}
              autoComplete="name"
            />
            <Field
              id="d-email"
              label="Work email"
              required
              type="email"
              value={email}
              onChange={setEmail}
              autoComplete="email"
              placeholder="you@agency.gov"
            />
            <Field
              id="d-phone"
              label="Phone"
              type="tel"
              value={phone}
              onChange={setPhone}
              autoComplete="tel"
              placeholder="555-123-4567"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field
                id="d-org"
                label="Agency / Organization"
                required
                value={organization}
                onChange={setOrganization}
                autoComplete="organization"
              />
              <Field
                id="d-role"
                label="Your role"
                value={role}
                onChange={setRole}
                placeholder="Training officer"
              />
            </div>
            <div>
              <label
                htmlFor="d-vertical"
                className="block text-xs font-semibold text-steel-700 uppercase tracking-wider"
              >
                Vertical
              </label>
              <select
                id="d-vertical"
                required
                value={vertical}
                onChange={(e) => setVertical(e.target.value)}
                className="mt-1.5 block w-full rounded-md border border-steel-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              >
                <option value="">Select one&hellip;</option>
                <option value="law_enforcement">Law enforcement</option>
                <option value="education">K-12 / Higher ed</option>
                <option value="fire_ems">Fire / EMS</option>
                <option value="healthcare">Healthcare</option>
                <option value="corporate_security">Corporate security</option>
                <option value="emergency_management">Emergency management</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label
                htmlFor="d-notes"
                className="block text-xs font-semibold text-steel-700 uppercase tracking-wider"
              >
                What are you trying to train? <span className="font-normal text-steel-400 normal-case">(optional)</span>
              </label>
              <textarea
                id="d-notes"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Active-threat tabletop, mass-casualty drill, EOC activation…"
                className="mt-1.5 block w-full rounded-md border border-steel-300 bg-white px-3 py-2.5 text-sm placeholder:text-steel-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 resize-none"
              />
            </div>

            {status === 'error' && (
              <p className="text-sm text-ember-600">{errorMsg}</p>
            )}

            <div className="pt-2 flex flex-col sm:flex-row-reverse gap-3">
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="btn btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {status === 'submitting' ? 'Submitting…' : 'Request demo'}
              </button>
              <button
                type="button"
                onClick={reset}
                className="btn btn-outline-dark"
              >
                Cancel
              </button>
            </div>
            <p className="text-xs text-steel-500 text-center">
              By submitting you agree to be contacted by Opinix.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({
  id,
  label,
  type = 'text',
  required = false,
  value,
  onChange,
  autoComplete,
  placeholder,
}: {
  id: string;
  label: string;
  type?: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  placeholder?: string;
}): JSX.Element {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-xs font-semibold text-steel-700 uppercase tracking-wider"
      >
        {label}
        {required && <span className="text-ember-500 ml-0.5">*</span>}
      </label>
      <input
        id={id}
        type={type}
        required={required}
        autoComplete={autoComplete}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 block w-full rounded-md border border-steel-300 bg-white px-3 py-2.5 text-sm placeholder:text-steel-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
      />
    </div>
  );
}
